import { createContext, useContext, useEffect, useRef, useState } from "react";
import logger from "shared/utils/logging";

const Logger = logger;

const MediaManagerContext = createContext();

export const MediaManagerProvider = (props) => {
  const children = props.children;

  const [mediaError, setMediaError] = useState(null);
  const [mediaAllowed, setMediaAllowed] = useState(null);
  const [availableCameras, setAvailableCameras] = useState(null);
  const [availableMicrophones, setAvailableMicrophones] = useState(null);

  const getMediaInputDevices = async () => {
    return navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        let deviceArray = new Array();
        devices.forEach((device) => {
          if (device.kind == "audioinput" || device.kind == "videoinput") {
            deviceArray.push(device);
          }
        });

        return deviceArray;
      })
      .catch((err) => {
        Logger.error(
          "Cannot get devices -> error name: " + err.name + ": " + err.message
        );
        throw err;
      });
  };

  const updateAvailableDevices = () => {
    getMediaInputDevices().then((devices) => {
      // const microphones = devices.filter(d => d.kind == "audioinput");
      // const cameras = devices.filter(d => d.kind == "videoinput");
      var microphones = new Array();
      var cameras = new Array();
      devices.forEach((device) => {
        if (device.kind == "audioinput") {
          microphones.push(device);
        } else if (device.kind == "videoinput") {
          cameras.push(device);
        }
      });
      setAvailableCameras(cameras);
      setAvailableMicrophones(microphones);
    });
  };

  const trackDeviceChange = () => {
    navigator.mediaDevices.ondevicechange = (dv, e) => {
      console.log("device changed");
      updateAvailableDevices();
    };
  };

  useEffect(() => {
    const permissionErrors = checkWebRTCPermissionErrors();
    if (permissionErrors != null) {
      setMediaError(permissionErrors);
      setMediaAllowed(false);
    } else {
      setMediaError(null);
      setMediaAllowed(true);
      // init devices list
      updateAvailableDevices();
      trackDeviceChange();
    }
  }, []);

  function checkWebRTCPermissionErrors() {
    if (typeof navigator.mediaDevices == "undefined") {
      return "Cannot open mic or camera because of unsecure context. Please use https";
    }

    if (navigator.mediaDevices == undefined || navigator.mediaDevices == null) {
      return "getUserMediaIsNotAllowed";
    }
    return null;
  }

  const getLocalMediaStream = (mediaConstraint) => {
    return new Promise((resolve, reject) => {
      const defaultMediaConstraint = {
        video: false,
        audio: false,
      };
      if (
        typeof navigator.mediaDevices == "undefined" ||
        navigator.mediaDevices == undefined ||
        navigator.mediaDevices == null
      ) {
        console.log(
          "There is media Devices found. MediaDevices=",
          navigator.mediaDevices
        );
        resolve(null);
      }
      navigator.mediaDevices
        .getUserMedia(mediaConstraint)
        .then((stream) => {
          resolve(stream);
        })
        .catch((er) => {
          reject(er);
        });
    });
  };

  /**
   * Called to get user media (camera and/or mic)
   *
   * @param {*} mediaConstraints : media constaint
   * @param {*} func : callback on success. The stream which is got, is passed as parameter to this function
   * @param {*} catch_error : error is checked if catch_error is true
   */
  const navigatorUserMedia = async (mediaConstraints, func, catch_error) => {
    if (
      mediaConstraints.video == "dummy" ||
      mediaConstraints.audio == "dummy"
    ) {
      var stream = new MediaStream();

      if (mediaConstraints.audio == "dummy") {
        stream.addTrack(getSilentAudioTrack());
      }

      if (mediaConstraints.video == "dummy") {
        stream.addTrack(this.getBlackVideoTrack());
      }

      return new Promise((resolve, reject) => {
        resolve(stream);
      });
    } else {
      try {
        const stream_1 = await navigator.mediaDevices.getUserMedia(
          mediaConstraints
        );
        if (typeof func != "undefined" || func != null) {
          func(stream_1);
        }
        return stream_1;
      } catch (error) {
        if (catch_error == true) {
          if (error.name == "NotFoundError") {
            this.getDevices();
          } else {
            this.callbackError(error.name, error.message);
          }
        } else {
          Logger.warn(error);
        }
        //throw error if there is a promise
        throw error;
      }
    }
  };

  const refAudioContext = useRef(null);
  const refOscillator = useRef(null);
  const refSilentAudioTrack = useRef(null);

  /**
   * Silent audio track
   */
  const getSilentAudioTrack = () => {
    stopSilentAudioTrack();
    if (refAudioContext.current == null) {
      refAudioContext.current = new (window.AudioContext ||
        window.webkitAudioContext)();
    }
    refOscillator.current = refAudioContext.current.createOscillator();

    const audioContext = refAudioContext.current;
    const oscillator = refOscillator.current;

    let dst = oscillator.connect(audioContext.createMediaStreamDestination());
    oscillator.start();
    refSilentAudioTrack.current = dst.stream.getAudioTracks()[0];

    const silentAudioTrack = refSilentAudioTrack.current;
    return silentAudioTrack;
  };

  const stopSilentAudioTrack = () => {
    if (refOscillator.current != null) {
      const oscillator = refOscillator.current;
      oscillator.stop();
      oscillator.disconnect();
      refOscillator.current = null;
    }

    if (refSilentAudioTrack.current != null) {
      const silentAudioTrack = refSilentAudioTrack.current;
      silentAudioTrack.stop();
      refSilentAudioTrack.current = null;
    }
  };

  /**
   * Black Video track
   */
  const getBlackVideoTrack = ({ width = 320, height = 240 }) => {
    const canvas = Object.assign(document.createElement("canvas"), {
      width,
      height,
    });
    const ctx = canvas.getContext("2d");
    const stream = canvas.captureStream();

    //We need to send black frames within a time interval, because when the user turn off the camera,
    //player can't connect to the sender since there is no data flowing.
    //Sending a black frame in each 3 seconds resolves it.
    const synthesisVideoInterval = setInterval(() => {
      ctx.fillRect(0, 0, width, height);
    }, 3000);

    stream.getVideoTracks()[0].onended = (evt) => {
      clearInterval(synthesisVideoInterval);
    };
    return stream;
  };

  const ret = {
    mediaAllowed,
    mediaError,
    availableCameras,
    availableMicrophones,
    getBlackVideoTrack,
  };
  return (
    <MediaManagerContext.Provider value={ret}>
      {children}
    </MediaManagerContext.Provider>
  );
};

export const useMediaManager = () => useContext(MediaManagerContext);
